/**
 * Shell Settings - notification channel mapping.
 *
 * The backend stores a user's channels under `notification_methods.Default`:
 *
 *   { sms: bool, email: bool, push: bool, whatsapp: bool }
 *
 * `Default` is the only profile this UI authors, and the only one
 * message-dispatcher looks up when it builds a fan-out. The shape mirrors
 * user-management/src/constants/roles.js on purpose: the admin form and a
 * user's own Settings page must read and write exactly the same thing.
 */

/**
 * The channels the Events section renders, in display order.
 *
 * `id` is the BACKEND field name. App notification used to be `app` here, which
 * matched nothing in `notification_methods` - the checkbox wrote to a key no
 * one reads. The id is now `push`; the LABEL is unchanged.
 *
 * `defaultEnabled` MUST stay equal to `DEFAULT_USER_FORM` in
 * user-management/src/constants/roles.js. It is not a display preference any
 * more: it is the value written for a user who has no stored key for that
 * channel, so a mismatch means this page and the admin form disagree about
 * what an unset channel means - and the first save here would persist the
 * disagreement. Email was `false` here while the admin form defaulted it to
 * `true`, which would have silently opted long-standing users out of email.
 */
export const NOTIFICATION_CHANNELS = [
    { id: 'push',     labelKey: 'settings.events.notificationTypes.app',      defaultEnabled: true  },
    { id: 'sms',      labelKey: 'settings.events.notificationTypes.sms',      defaultEnabled: false },
    { id: 'email',    labelKey: 'settings.events.notificationTypes.email',    defaultEnabled: true  },
    { id: 'whatsapp', labelKey: 'settings.events.notificationTypes.whatsapp', defaultEnabled: false }
];

/** Channels that can only be delivered if the profile carries a phone number. */
export const PHONE_CHANNELS = ['sms', 'whatsapp'];

/**
 * Stored `notification_methods` -> checkbox state.
 *
 * Each channel falls back to its OWN default rather than to `false`, so a user
 * stored before a channel existed keeps the behaviour they had. The alternative
 * would silently switch App notifications off for everyone predating WhatsApp.
 */
export const toChannelState = (notificationMethods) => {
    const stored = notificationMethods?.Default ?? {};

    return Object.fromEntries(
        NOTIFICATION_CHANNELS.map(({ id, defaultEnabled }) => [
            id,
            typeof stored[id] === 'boolean' ? stored[id] : defaultEnabled
        ])
    );
};

/**
 * Checkbox state -> the `update_user_settings` payload fragment.
 *
 * Anything already stored is carried through: other profiles beside `Default`,
 * and any key inside `Default` this build does not know about. A save from this
 * page only ever changes the four channels it renders.
 */
export const toNotificationMethods = (channels, existing) => ({
    ...(existing ?? {}),
    Default: {
        ...(existing?.Default ?? {}),
        ...Object.fromEntries(
            NOTIFICATION_CHANNELS.map(({ id }) => [id, Boolean(channels[id])])
        )
    }
});

/** True when the two states differ on any rendered channel. */
export const channelsDiffer = (a, b) =>
    NOTIFICATION_CHANNELS.some(({ id }) => Boolean(a[id]) !== Boolean(b[id]));
